import { PulseLoader } from "@/components/pulse_loader";
import classNames from "classnames";
import React from "react";
import { colors } from "../colors";

import styles from "./date_range_picker_date.module.css";
import { useMediaQuery } from "@/hooks";

interface DateRangePickerDateProps {
  date: number;
  isDateInBetween: boolean;
  isHighlighted: boolean;
  // Tinted as one of the dates of a recurring booking being drafted.
  isMarked?: boolean;
  isDateFaded: boolean;
  price?: string;
  isDiscountPrice?: boolean;
  isSameFirstDate: boolean;
  isSameSecondDate: boolean;
  onClick: () => void;
  showPrice?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isLoading?: boolean;
}

export function DateRangePickerDate({
  date,
  isDateInBetween,
  isHighlighted,
  isMarked,
  isDateFaded,
  price,
  isDiscountPrice,
  isSameFirstDate,
  isSameSecondDate,
  onClick,
  showPrice,
  onMouseEnter,
  onMouseLeave,
  isLoading,
}: DateRangePickerDateProps) {
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";

  const renderPrice = () => {
    if (!showPrice) {
      return;
    }
    // The price is now server-owned, so it lands one request after the grid
    // paints instead of being derived locally. Reserve the line either way,
    // or every day number shifts up a row when the rates arrive.
    if (!price) {
      return <div className={styles.price} aria-hidden="true" />;
    }
    return (
      <div
        className={classNames(
          styles.price,
          isDiscountPrice && styles.discountPrice
        )}
      >
        {price}
      </div>
    );
  };

  const dateTestId =
    "date-picker-v2-day-button-id" +
    (isHighlighted
      ? "-highlighted"
      : isDateInBetween
      ? "-highlighted-between"
      : "");

  return (
    <div
      className={`${styles.dayCellWrapper} ${
        isDateInBetween && `${styles.dayInBetween} day-in-between`
      }`}
      onMouseEnter={!isMobile ? onMouseEnter : undefined}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={`${styles.dayPriceWrapper} ${
          isHighlighted && `${styles.dayCellHighlighted} day-cell-higlighted`
        } ${
          isMarked &&
          !isHighlighted &&
          `${styles.dayCellMarked} day-cell-marked`
        } ${
          isDateFaded
            ? `${styles.dayCellFaded} day-cell-disabled`
            : "date-picker-v2-day-button"
        }`}
        onClick={onClick}
        data-testid={dateTestId}
      >
        <div className={styles.dayCell}>{date}</div>
        {isLoading && <PulseLoader size={4} color={colors.blackMinus70} />}
        {renderPrice()}
      </div>
      {(isSameFirstDate || isSameSecondDate) && (
        <div
          className={`${
            isSameFirstDate && `${styles.firstClickedDay} first-clicked-day`
          } ${
            isSameSecondDate && `${styles.secondClickedDay} second-clicked-day`
          }`}
        />
      )}
    </div>
  );
}
